import { ReactNode } from "react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/shadcn/dialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Button, ButtonVariant } from "@/components/shadcn/button";

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerText?: string;
  triggerIcon?: IconProp;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  onConfirm: () => void;
  disableConfirm?: boolean;
  triggerVariant?: ButtonVariant;
  disableTrigger?: boolean;
  hideTrigger?: boolean;
  triggerTitle?: string;
  triggerIconCss?: string;
  triggerIconCssDisabled?: string;
}

export const ConfirmDialog = ({open, onOpenChange, triggerText, triggerIcon, triggerIconCss, triggerIconCssDisabled, title, description, confirmText = "Confirm", cancelText = "Cancel", loading = false, onConfirm, disableConfirm = false, triggerVariant = "outline", disableTrigger = false, hideTrigger = false, triggerTitle}: ConfirmDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {hideTrigger ? null : !disableTrigger ? (
        <DialogTrigger asChild>
            <Button variant={triggerVariant} size="sm" title={triggerTitle}>
              {triggerIcon && <FontAwesomeIcon icon={triggerIcon} className={triggerIconCss} />}
              {triggerText}
            </Button>
        </DialogTrigger>
      ) : (
        // If the trigger is disabled dont wrap in DialogTrigger as any clicked child triggers the dialog, 
        // regardless of the child being disabled
        <span title={triggerTitle}>
          <Button variant={triggerVariant} size="sm" disabled>
            {triggerIcon && <FontAwesomeIcon icon={triggerIcon} className={triggerIconCssDisabled} />}
            {triggerText}
          </Button>
        </span>
      )}
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {description && <div className="text-muted-foreground mt-2">{description}</div>}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" type="button" disabled={loading}>
              {cancelText}
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            type="button"
            disabled={loading || disableConfirm}
            onClick={onConfirm}
          >
            {loading ? "Processing..." : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}